import { styled } from "goober";
import { useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { Button } from "../Button";
import { ConfirmModal } from "../ConfirmModal";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { Card } from "./shared";

const HeadingRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 8px;
`;

const HeadingText = styled("div")`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DangerButton = styled(Button)`
  &&& {
    background: var(--red-200);
    color: var(--red-600);
  }
  &&&:hover:not(:disabled) {
    background: var(--red-400);
  }
`;

const NeutralButton = styled(Button)`
  &&& {
    background: var(--alpha4);
  }
  &&&:hover:not(:disabled) {
    background: var(--alpha8);
  }
`;

type Confirm = {
  title?: string;
  description?: ReactNode;
  confirmLabel?: string;
  children?: ReactNode;
  onConfirm: () => void;
};

type Props = {
  title: string;
  description: ReactNode;
  actionLabel: string;
  tone?: "danger" | "neutral";
  disabled?: boolean;
  disabledReason?: string;
  confirm?: Confirm;
  renderConfirm?: (args: { open: boolean; onClose: () => void }) => ReactNode;
  onAction?: () => void;
  style?: CSSProperties;
};

export function DangerSection({
  title,
  description,
  actionLabel,
  tone = "danger",
  disabled,
  disabledReason,
  confirm,
  renderConfirm,
  onAction,
  style,
}: Props) {
  const [open, setOpen] = useState(false);

  const onClick = () => {
    if (confirm || renderConfirm) setOpen(true);
    else onAction?.();
  };

  const button =
    tone === "danger" ? (
      <DangerButton
        size="l"
        variant="secondary"
        disabled={disabled}
        onClick={onClick}
      >
        {actionLabel}
      </DangerButton>
    ) : (
      <NeutralButton
        size="l"
        variant="secondary"
        disabled={disabled}
        onClick={onClick}
      >
        {actionLabel}
      </NeutralButton>
    );

  return (
    <Card style={style}>
      <HeadingRow>
        <HeadingText>
          <Text variant="text-regular-md">{title}</Text>
          <Text variant="text-small" color="var(--text-secondary)">
            {description}
          </Text>
        </HeadingText>
        {disabled && disabledReason != null ? (
          <Tooltip content={disabledReason}>{button}</Tooltip>
        ) : (
          button
        )}
      </HeadingRow>
      {confirm != null && (
        <ConfirmModal
          open={open}
          onClose={() => setOpen(false)}
          onConfirm={() => {
            if (disabled) return;
            confirm.onConfirm();
            setOpen(false);
          }}
          confirmDisabled={disabled}
          title={confirm.title ?? title}
          description={confirm.description ?? description}
          confirmLabel={confirm.confirmLabel ?? actionLabel}
          destructive={tone === "danger"}
        >
          {confirm.children}
        </ConfirmModal>
      )}
      {renderConfirm?.({ open, onClose: () => setOpen(false) })}
    </Card>
  );
}
